/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React, { useEffect } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useQuery } from '@apollo/client';

import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import {
    array,
    arrayOf,
    func, object, shape, string,
} from 'prop-types';
import { GRAPHQL_ENV } from '../../../gql';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import findProductPageByPartNumber from '../../../gql/queries/findProductPageByPartNumber';
import GraphqlCategorySkeleton from '../GraphqlSkeletonComponents/CategoryPageSkeleton/CategoryPageSkeleton';
import GraphqlCategoryBuilder from './GraphqlCategoryBuilder';
import { setPassportBundleCollectionData } from '../../../../state/ducks/Passport/Passport-Action';
import getCollectionBannerData from './Partials/getCollectionBannerData/getCollectionBannerData';
import getCollectionBannerV2Data from './Partials/getCollectionBannerData/getCollectionBannerV2Data';
import { setProductFilterOccasion } from '../../../../state/ducks/App/App-Actions';
import SisterBrandProduct from './SisterBrandProducts/SisterBrandProducts';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getIsBot } from '../../../../state/ducks/App/App-Selectors';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import PassportFacetAbTestContext from './PassportFacetAbTestContext';
import useExperience from '../../../helpers/Personalization/useExperience';

const GraphqlPassportCategoryPageContainer = ({
    page, categoryData,
    brand, presentation_family,
    passportBundleCollection, setOccasion,
    location, track, trackCustomClickStream,
}) => {
    const isBot = useSelector(getIsBot);
    const categoryBlocks = categoryData?.content?.entries?.[0]?.category_blocks;
    let passportSkuDetails = {};
    let passportDetails = {};
    (categoryBlocks || []).forEach((block) => {
        if (block.passport_bundle_product) {
            passportDetails = block.passport_bundle_product;
            passportSkuDetails = block.passport_bundle_product?.passport_referenced_sku?.[0];
        }
    });

    const collectionBannerData = getCollectionBannerData(brand, GRAPHQL_ENV, page.path);
    const collectionBannerV2Data = getCollectionBannerV2Data(brand, GRAPHQL_ENV, page.path);

    if (passportSkuDetails?.passport_item_sku && passportSkuDetails?.item_sku && passportSkuDetails?.price_type) {
        const PRODUCT_BY_PART = findProductPageByPartNumber(brand, passportSkuDetails?.item_sku);
        const { loading, error, data } = useQuery(PRODUCT_BY_PART);

        if (loading) {
            return <GraphqlCategorySkeleton />;
        }

        if (error) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                component: 'GraphqlPassportCategoryPageContainer.js',
                message: `Error loading findProductPageByPartNumber from url "${page?.path}" passportPartNumber "${passportSkuDetails?.item_sku}"`,
                env: GRAPHQL_ENV,
                error,
            });
        }

        if (!data) {
            mbpLogger.logWarning({
                appName: process.env.npm_package_name,
                query: PRODUCT_BY_PART,
                component: 'GraphqlPassportCategoryPageContainer.js',
                message: `No data returned passportSKU "${passportSkuDetails?.item_sku}"`,
                env: GRAPHQL_ENV,
            });
        }

        if (data?.findProductPageByPartNumber?.product?.productSkus.length > 0) {
            const passportSkus = data?.findProductPageByPartNumber?.product?.productSkus || [];
            let collectionDetail = {};
            passportSkus.forEach((sku) => {
                if (sku?.id === passportSkuDetails?.passport_item_sku) {
                    const prices = sku?.prices || [];
                    prices.forEach((selectedSku) => {
                        if (selectedSku?.type === passportSkuDetails?.price_type) {
                            collectionDetail = {
                                enabled: true,
                                price: selectedSku?.value,
                                snipeImage: passportDetails?.snipe_image?.url,
                            };
                        }
                    });
                }
            });
            passportBundleCollection(collectionDetail);
        }
    } else {
        passportBundleCollection({ enabled: false });
    }

    let catData = categoryData;
    if (categoryData) {
        /**
         * New apollo version not allow to updating the object.
         */
        catData = JSON.parse(JSON.stringify(categoryData));
    }
    /**
    * This is using fo GA events
    */
    if (categoryData.category && !categoryData.category?.name) {
        catData.category.name = categoryData?.content?.entries?.[0]?.name || '';
    }

    const isPassportEligibleFacetForceEnabled = useSelector(getFeatureFlag('is-passport-eligible-facet-force-enabled'));
    const isPassportEligibleFacetABTesting = useSelector(getFeatureFlag('is-passport-eligible-facet-ab-testing'));
    const isProductListABTestEnabled = useSelector(getFeatureFlag('is-product-list-ab-test-enabled'));
    const pageType = useSelector(getCurrentPageType);

    const {
        error, data,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'ShowPassportEligibleFacet',
        interactionName: `${brand.domain} - Get Campaign - Show Passport Eligible Facet`,
        featureFlag: isPassportEligibleFacetABTesting,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    if (error) {
        mbpLogger.logError({
            function: 'CategoryPageContainer',
            message: 'Error loading API data from ShowPassportEligibleFacet',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    useClickStreamCustomEventsTracking({
        salesforceResponse: data?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (isPassportEligibleFacetABTesting && data?.contentSF?.campaign?.campaignResponses?.length) {
            const campaign = data?.contentSF?.campaign?.campaignResponses[0];
            const eventLabel = `${campaign?.payload?.userGroup?.toLowerCase() === 'test'  ? 'Variant' : 'Control'}`;
            track({
                eventCategory: 'Experiment',
                eventAction: 'Passport Eligible In Filter Only',
                eventLabel,
                experienceId: campaign?.experienceId,
                campaignId: campaign?.campaignId,
                variant: campaign?.experienceName,
            });
        }
    }, [data]);

    const {
        loading: productListCountLoading, error: productListCountError, data: productListCountData, variables: productListCountVariables,
    } = useExperience({
        queryName: 'ProductListCount',
        interactionName: `${brand.domain}-product-list-count`,
        featureFlag: isProductListABTestEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
        type: 'experiment',
    });

    if (productListCountError) {
        mbpLogger.logError({
            function: 'CategoryPageContainer',
            message: 'Error loading API data from ProductListCount',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: productListCountError,
        });
    }

    useClickStreamCustomEventsTracking({
        experimentTracking: productListCountData?.experience?.experimentTracking,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (isProductListABTestEnabled && productListCountData?.experience?.experiment?.featureId) {
            track({
                eventCategory: 'Experiment',
                eventAction: 'No Of Collection Products Collection Page',
                eventLabel: productListCountData?.experience?.experiment?.name,
                experienceId: productListCountData?.experience?.experimentTracking?.id,
                campaignId: productListCountData?.experience?.experimentTracking?.campaignId,
                variant: productListCountData?.experience?.experimentTracking?.name,
            });
        }
    }, [productListCountData]);

    const passportEligibleFacetGroup = data?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    const campaignPageSize = productListCountData?.experience?.experiment?.value?.category ?? {};

    const gnavBarEnabled = useSelector(getFeatureFlag('is-gnav-bar-enabled'));
    const { loading: gnavCollectionLoadings, variables: gnavCollectionVariables, data: gnavCollectionData } = useSalesforcePersonalizedContentQuery({
        queryName: 'gnavBarCollection',
        interactionName: `${brand?.domain} - All-PLP-GNAV Bar-v2`,
        featureFlag: gnavBarEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const gnavBarCollectionEnable = gnavCollectionData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup; // userGroup Variant from SF

    useClickStreamCustomEventsTracking({
        salesforceResponse: gnavCollectionData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (typeof gnavCollectionData !== 'undefined' && typeof window !== 'undefined' && !window.wuGnavBarEventFired && gnavCollectionData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Guided-Nav-Bar-Test',
                eventLabel: gnavBarCollectionEnable?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_detail: gnavBarCollectionEnable?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_element: 'Guided-Nav-Bar-Test',
                feature_category: 'Experiment',
            };
            track(eventParam);
            window.wuGnavBarEventFired = true;
        }
        return () => {
            window.wuGnavBarEventFired = false;
        };
    }, [gnavCollectionData]);
    const trackPassportEligibleFacet = () => {
        if (data?.contentSF?.campaign) {
            trackCustomClickStream({
                action: 'Click',
                salesforceResponse: data?.contentSF,
                page: { type: pageType },
                eventSubType: 'campaign-tracking',
                label: 'Passport Filter Applied',
                category: 'Category Page',
            });
        }
        track({
            eventCategory: 'Category Page',
            eventAction: 'Filters',
            eventLabel: 'Passport Filter Applied',
        });
    };
    if (gnavCollectionLoadings || gnavCollectionVariables?.skip || productListCountLoading || productListCountVariables?.skip) {
        return <GraphqlCategorySkeleton />;
    }
    return (
        <SisterBrandProduct categoryId={categoryData?.category?.id || ''}>
            <PassportFacetAbTestContext.Provider value={{
                showPassportEligibleFacet: passportEligibleFacetGroup?.toLowerCase() === 'test' || isPassportEligibleFacetForceEnabled,
                trackPassportEligibleFacet,
            }}
            >
                <GraphqlCategoryBuilder
                    location={location}
                    page={page}
                    categoryData={catData}
                    brand={brand}
                    presentation_family={presentation_family}
                    setOccasion={setOccasion}
                    collectionBannerData={collectionBannerData}
                    collectionBannerV2Data={collectionBannerV2Data}
                    guidedNavBarEnabled={gnavBarCollectionEnable?.toLowerCase() === 'test'}
                    campaignPageSize={campaignPageSize}
                    campaignLoading={productListCountLoading || productListCountVariables?.skip}
                />
            </PassportFacetAbTestContext.Provider>
        </SisterBrandProduct>
    );
};

GraphqlPassportCategoryPageContainer.propTypes = {
    location: object.isRequired,
    page: shape({
        path: string,
    }),
    categoryData: shape({
        content: shape({
            entries: arrayOf(shape({
                category_blocks: array,
                guided_nav: object,
            })),
        }),
    }),
    brand: string,
    presentation_family: string,
    passportBundleCollection: func,
    setOccasion: func,
    track: func,
    trackCustomClickStream: func,
};

GraphqlPassportCategoryPageContainer.defaultProps = {
    page: {},
    categoryData: {
        content: {
            entries: [
                {
                    category_blocks: [],
                    guided_nav: {},
                },
            ],
        },
    },
    brand: '',
    presentation_family: '',
    passportBundleCollection: () => {},
    setOccasion: () => {},
    track: () => {},
    trackCustomClickStream: () => {},
};

const mapDispatchTpProps = (dispatch) => ({
    passportBundleCollection: bindActionCreators(setPassportBundleCollectionData, dispatch),
    setOccasion: bindActionCreators(setProductFilterOccasion, dispatch),
    track: bindActionCreators(trackEvent, dispatch),
    trackCustomClickStream: bindActionCreators(emitCustomTrackingEvent, dispatch),
});

const enhance = compose(
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
        updateEveryRender: true,
    }),
    connect(null, mapDispatchTpProps),
);

export default enhance(GraphqlPassportCategoryPageContainer);
